/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Action, State, fromTreeToList } from '@table-library/react-table-library/common';
import { Body, Cell, Data, Header, HeaderCell, HeaderRow, Row, Table } from '@table-library/react-table-library/table';
import { useTheme } from '@table-library/react-table-library/theme';
import { CellTree, useTree, TreeExpandClickTypes, Tree } from '@table-library/react-table-library/tree';
import { useRowSelect, SelectTypes, Select, SelectClickTypes } from '@table-library/react-table-library/select';
import { Button, Input } from 'reactstrap';
import G17Client from '../../../services/G17Client';
import { CustomReportSubsidiaryStats } from '../../../types/custom-report';
import Loader from '../../loader';
import { buildList, buildTree, filterData, findMaxDepth, getExpandedIds } from '../util/treeNavigation';
import { FilterSelect } from './FilterSelect';
import { useDebouncedCallback } from 'use-debounce';

export interface SubsidiarySelectionTableProps {
  initiativeId: string;
  handleSetSelectedIds?: (ids: string[]) => void;
  selectedIds?: string[];
  isDisabled?: boolean
}

const getSubsidiaryLevelOptions = (depth: number) => {
  return Array.from(Array(depth).keys()).map((level) => ({
    label: `Level ${level} ${level === 0 ? ' (Organization)' : ''}`,
    value: String(level),
  }));
};

export const SubsidiarySelectionTable = (props: SubsidiarySelectionTableProps) => {
  const { initiativeId, selectedIds, handleSetSelectedIds = () => { }, isDisabled = false } = props;

  const [subsidiaryList, setSubsidiaryList] = useState<CustomReportSubsidiaryStats[]>([]);
  const [tableData, setTableData] = useState<Data>({ nodes: [] });
  const [filteredData, setFilterData] = useState<Data>({ nodes: [] });
  const [search, setSearch] = React.useState('');
  const [filterLevel, setFilterLevel] = React.useState('');

  const expandedIds = useMemo(() => {
    return getExpandedIds(subsidiaryList, initiativeId, selectedIds || [initiativeId]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subsidiaryList]);

  const handleSearch = useDebouncedCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    // debounce this
    setFilterData({ nodes: filterData(tableData.nodes, search) });
    const dataIds = fromTreeToList(filteredData.nodes).map((item) => item.id);
    tree.fns.onAddAll(dataIds);
  }, 500);

  const theme = useTheme({
    Table: `
      height: unset;
      max-height: 100%;
      --data-table-library_grid-template-columns: 40% repeat(2, minmax(0, 2fr)) repeat(2, minmax(0, 1fr));
    `,
  });

  useEffect(
    () => setFilterData({
      nodes: filterData(tableData.nodes, search),
    }),
    [search, tableData.nodes]
  );

  const maxDepth = useMemo(() => findMaxDepth(subsidiaryList, initiativeId), [subsidiaryList, initiativeId]);

  const subsidiaryLevelOptions = getSubsidiaryLevelOptions(maxDepth);

  const tree = useTree(filteredData,
    {
      state: { ids: expandedIds },
    },
    {
      treeIcon: {
        margin: '4px',
        iconDefault: <i className='fa ml-2 mr-3' />,
        iconRight: <i className='fa fa-caret-right text-ThemeAccentExtradark mx-2' />,
        iconDown: <i className='fa fa-caret-down text-ThemeAccentExtradark mx-2' />,
      },
      clickType: TreeExpandClickTypes.RowClick,
    }
  );

  const select = useRowSelect(filteredData,
    {
      state: { ids: selectedIds ?? [], isDisabled },
      onChange: onSelectChange,
    },
    {
      // On parent node click, when partially selected true=add_all, false=remove_all
      isPartialToAll: true,
      rowSelect: SelectTypes.MultiSelect,
      clickType: SelectClickTypes.ButtonClick,
    }
  );

  function onSelectChange(action: Action, state: State) {
    handleSetSelectedIds(state.ids);
  }

  useEffect(() => {
    if (filterLevel) {
      setTableData(buildList(subsidiaryList, initiativeId, Number(filterLevel)));
    } else {
      setTableData(buildTree(subsidiaryList, initiativeId));
    }
  }, [subsidiaryList, filterLevel, initiativeId]);

  useEffect(() => {
    G17Client.customReportGetSubsidiaryStats(initiativeId).then(subsidiaryList => {
      setSubsidiaryList(subsidiaryList);
    });
  }, [initiativeId]);

  if (!tableData) {
    return <Loader />
  }

  const checkIsToggleAll = (state: State): boolean => {
    if (filterLevel === '') {
      return state.ids.length < subsidiaryList.length;
    }
    // list of ids that is both visible on the screen and currently expanded or selected
    const matchFilterIds = fromTreeToList(filteredData.nodes).filter((item) => state.ids.includes(item.id));
    return matchFilterIds.length < filteredData.nodes.length;
  };

  const handleToggleTree = (tree: Tree) => {
    const dataIds = fromTreeToList(filteredData.nodes).map((item) => item.id);
    if (checkIsToggleAll(tree.state)) {
      tree.fns.onAddAll(dataIds);
    }
    else {
      tree.fns.onRemoveByIds(dataIds);
    }
  };

  const handleToggleSelect = (select: Select) => {
    // get all visible ids on the screen
    const dataIds = fromTreeToList(filteredData.nodes).map((item) => item.id);
    if (checkIsToggleAll(select.state)) {
      select.fns.onAddAll(dataIds);
      // expand the selected
      tree.fns.onAddAll(dataIds);
    }
    else {
      // only remove the visible ids on the screen
      select.fns.onRemoveByIds(dataIds);
    }
  }

  return (
    <>
      <div className='d-flex align-items-center'>
        <div className='mr-3' style={{ width: '300px' }}>
          <Input
            placeholder='Search for subsidiary...'
            type='text'
            className='border-ThemeBorderDefault text-md'
            defaultValue={search}
            onChange={handleSearch}
          />
        </div>
        <div className='mr-3'>
          <FilterSelect
            placeholder={'Subsidiary Level'}
            options={subsidiaryLevelOptions}
            value={filterLevel}
            onSelectChange={(value) => setFilterLevel(value)}
          />
        </div>
        <div className='ml-auto'>
          {`${select.state.ids.length ?? 0} subsidiaries selected`}
        </div>
      </div>

      <div className='mt-2 collapsible-table'
        style={{
          height: '400px',
        }}>
        <Table data={filteredData} tree={tree} select={select} theme={theme} layout={{ fixedHeader: true, custom: true }}>
          {(tableList) => (
            <>
              <Header>
                <HeaderRow>
                  <HeaderCell>Subsidiary name</HeaderCell>
                  <HeaderCell>Level</HeaderCell>
                  <HeaderCell>Number of surveys</HeaderCell>
                  <HeaderCell>
                    <Button
                      disabled={isDisabled}
                      color='link'
                      size='sm'
                      onClick={() => handleToggleTree(tree)}
                      className='p-0'
                    >
                      {checkIsToggleAll(tree.state) ? 'Expand all' : 'Collapse all'}
                    </Button>
                  </HeaderCell>
                  <HeaderCell className='header-button'>
                    <Button
                      disabled={isDisabled}
                      color='link'
                      size='sm'
                      onClick={() => handleToggleSelect(select)}
                      className='p-0'
                    >
                      {checkIsToggleAll(select.state) ? 'Select all' : 'Unselect all'}
                    </Button>
                  </HeaderCell>
                </HeaderRow>
              </Header>

              <Body>
                {tableList.map((item) => (
                  <Row disabled={isDisabled} key={item.id} item={item}>
                    <CellTree item={item}>{item.name}</CellTree>
                    <Cell>{item.level}</Cell>
                    <Cell>{item.surveyCount}</Cell>
                    <Cell />
                    <Cell>
                      {isDisabled ? (
                        <Input disabled type='checkbox' checked={select.state.ids.includes(item.id)} />
                      ) : (
                        <Input
                          type='checkbox'
                          onChange={() => select.fns.onToggleById(item.id)}
                          checked={select.state.ids.includes(item.id)}
                        />
                      )}
                    </Cell>
                  </Row>
                ))}
              </Body>
            </>
          )}
        </Table>
      </div>
    </>
  );
};
